/**
 * Shared password validation utilities for user management
 * Contains common validation functions used across UserForm and PasswordResetModal components
 */

/**
 * Validates password format according to application requirements
 * @param {string} password - The password to validate
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export function validatePasswordFormat(password, $t) {
  if (!password) {
    return {
      isValid: false,
      error: $t('users.validation.passwordRequired')
    }
  }

  // Minimum length
  if (password.length < 8) {
    return {
      isValid: false,
      error: $t('users.validation.passwordTooShort')
    }
  }

  // Maximum length
  if (password.length > 60) {
    return {
      isValid: false,
      error: $t('users.validation.passwordTooLong')
    }
  }

  // ASCII characters only (no Vietnamese characters)
  if (!/^[\x00-\x7F]*$/.test(password)) {
    return {
      isValid: false,
      error: $t('users.validation.passwordAsciiOnly')
    }
  }

  // Must contain at least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    return {
      isValid: false,
      error: $t('users.validation.passwordNeedsUppercase')
    }
  }

  // Must contain at least one lowercase letter
  if (!/[a-z]/.test(password)) {
    return {
      isValid: false,
      error: $t('users.validation.passwordNeedsLowercase')
    }
  }

  // Must contain at least one number
  if (!/[0-9]/.test(password)) {
    return {
      isValid: false,
      error: $t('users.validation.passwordNeedsNumber')
    }
  }

  // Must contain at least one special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return {
      isValid: false,
      error: $t('users.validation.passwordNeedsSpecialChar')
    }
  }

  return {
    isValid: true,
    error: ''
  }
}

/**
 * Validates password confirmation matches the original password
 * @param {string} password - The original password
 * @param {string} confirmPassword - The confirmation password
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export function validatePasswordConfirmation(password, confirmPassword, $t) {
  if (!confirmPassword) {
    return {
      isValid: false,
      error: $t('users.validation.confirmPasswordRequired')
    }
  }

  if (password !== confirmPassword) {
    return {
      isValid: false,
      error: $t('users.validation.passwordsDoNotMatch')
    }
  }

  return {
    isValid: true,
    error: ''
  }
}

/**
 * Generates a random password that meets all validation requirements
 * This ensures the generated password will pass all validation rules
 * @returns {string} - A valid password that meets all requirements
 */
export function generateValidPassword() {
  const length = 12
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const numbers = '0123456789'
  const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?'

  let password = ''

  // Ensure at least one of each required type to guarantee validation passes
  password += uppercase.charAt(Math.floor(Math.random() * uppercase.length))
  password += lowercase.charAt(Math.floor(Math.random() * lowercase.length))
  password += numbers.charAt(Math.floor(Math.random() * numbers.length))
  password += symbols.charAt(Math.floor(Math.random() * symbols.length))

  // Fill the rest randomly from all character sets
  const allChars = lowercase + uppercase + numbers + symbols
  for (let i = password.length; i < length; i++) {
    password += allChars.charAt(Math.floor(Math.random() * allChars.length))
  }

  // Shuffle the password to avoid predictable patterns
  return password.split('').sort(() => Math.random() - 0.5).join('')
}

/**
 * Validates email format and length
 * @param {string} email - The email to validate
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export function validateEmailFormat(email, $t) {
  if (!email) {
    return {
      isValid: false,
      error: $t('users.validation.emailRequired')
    }
  }

  // Email length validation (check first for better performance)
  if (email.length > 1000) {
    return {
      isValid: false,
      error: $t('users.validation.emailTooLong')
    }
  }

  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  if (!emailRegex.test(String(email).toLowerCase())) {
    return {
      isValid: false,
      error: $t('users.validation.emailInvalid')
    }
  }

  // Extract domain part for additional validation
  const parts = email.split('@')
  if (parts.length !== 2) {
    return {
      isValid: false,
      error: $t('users.validation.emailInvalid')
    }
  }

  const domain = parts[1]

  // Check if domain starts or ends with hyphen (invalid)
  // Examples: email@-domain.com, email@domain-.com are invalid
  if (domain.startsWith('-') || domain.endsWith('-')) {
    return {
      isValid: false,
      error: $t('users.validation.emailInvalid')
    }
  }

  // Check each domain label (parts separated by dots)
  const domainLabels = domain.split('.')
  for (const label of domainLabels) {
    // Each label cannot start or end with hyphen
    if (label.startsWith('-') || label.endsWith('-')) {
      return {
        isValid: false,
        error: $t('users.validation.emailInvalid')
      }
    }

    // Each label cannot end with underscore
    // Examples: email@email_.com is invalid, but email@email_cyradar.com is valid
    if (label.endsWith('_')) {
      return {
        isValid: false,
        error: $t('users.validation.emailInvalid')
      }
    }

    // Each label must contain only alphanumeric, hyphen, or underscore
    // Underscore is allowed in the middle (e.g., email@cyradar_domain.com is valid)
    if (!/^[a-zA-Z0-9_-]+$/.test(label)) {
      return {
        isValid: false,
        error: $t('users.validation.emailInvalid')
      }
    }
  }

  return {
    isValid: true,
    error: ''
  }
}

/**
 * Validates username format and length
 * @param {string} username - The username to validate
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export function validateUsernameFormat(username, $t) {
  if (!username) {
    return {
      isValid: false,
      error: $t('users.validation.usernameRequired')
    }
  }

  // Length validation
  if (username.length < 3) {
    return {
      isValid: false,
      error: $t('users.validation.usernameTooShort')
    }
  }

  if (username.length > 30) {
    return {
      isValid: false,
      error: $t('users.validation.usernameTooLong')
    }
  }

  // No spaces validation
  if (username.includes(' ')) {
    return {
      isValid: false,
      error: $t('users.validation.usernameNoSpaces')
    }
  }

  // No special characters validation (only alphanumeric and underscore allowed)
  const usernameRegex = /^[a-zA-Z0-9_]+$/
  if (!usernameRegex.test(username)) {
    return {
      isValid: false,
      error: $t('users.validation.usernameInvalidChars')
    }
  }

  return {
    isValid: true,
    error: ''
  }
}
